/**
 * Account Aggregator Update Auth Key Drawer
 *
 * Allows updating outbound auth key.
 */

import React, { useEffect, useState } from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerOverlay, Button } from 'equal-ds-ui';
import type { AccountAggregatorUpdateAuthKeyDrawerProps } from '../../../types/account-aggregator';

export const AccountAggregatorUpdateAuthKeyDrawer: React.FC<AccountAggregatorUpdateAuthKeyDrawerProps> = ({
  isOpen,
  aggregator,
  onSubmit,
  onClose,
  isSubmitting,
}) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setValue(aggregator?.auth_Key_outbound || '');
      setError(null);
    }
  }, [isOpen, aggregator]);

  if (!isOpen || !aggregator) {
    return null;
  }

  const handleSubmit = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setError('Please enter outbound auth key.');
      return;
    }
    setError(null);
    await onSubmit(trimmed);
  };

  return (
    <>
      <DrawerOverlay />
      <Drawer variant="overlay" width="480px">
        <DrawerHeader className="border-b border-border-default pb-4" onClose={onClose}>
          <div>
            <h2 className="text-xl font-semibold text-text-primary">
              Update outbound auth key
            </h2>
            <p className="text-sm text-text-secondary mt-1">
              AA ID: {aggregator.account_aggregator_id}
            </p>
          </div>
        </DrawerHeader>
        <DrawerContent className="p-6 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Outbound auth key
            </label>
            <textarea
              value={value}
              onChange={(event) => setValue(event.target.value)}
              disabled={isSubmitting}
              className={`w-full min-h-[140px] rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                error
                  ? 'border-red-300 focus:ring-red-200'
                  : 'border-border-default focus:ring-primary-100'
              }`}
              placeholder="Enter outbound auth key"
            />
            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
          </div>
        </DrawerContent>
        <div className="px-6 py-4 border-t border-border-default flex justify-end gap-3 bg-background-secondary flex-shrink-0">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Updating...' : 'Update'}
          </Button>
        </div>
      </Drawer>
    </>
  );
};
